import axios from "axios";

const state = () => ({
  isLoading: false,
  allResources: [],
  allBooks: [],
  flattenedAllResources: [],
  flattenedBooks: [],
  sessions: [],
  filteredItems: [],
  filteredResources: {},
  showFilterModal: false,
  schedule: [],
  scheduleMapping: {}, // <session cms id>: schedule obj
  sessionIdScheduleMapping: {}, // <session id> : schedule obj
  filterTypeScheduleMapping: {},
  searchSuggestions: [],
  clickedFilter: {},
  selectedFilter: {},
  allBookMarkList: [], // uses by display with resources
  bookMarkMap: {},
});

const mutations = {
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setAllResources(state, allResources) {
    state.allResources = allResources;
  },
  setAllBooks(state, allBooks) {
    state.allBooks = allBooks;
  },
  setAllBookMarkList(state, allBookMarkList) {
    state.allBookMarkList = allBookMarkList;
  },
  setFlattenedAllResources(state, flattenedAllResources) {
    state.flattenedAllResources = flattenedAllResources;
  },
  setFlattenedBooks(state, flattenedBooks) {
    state.flattenedBooks = flattenedBooks;
  },
  setSessions(state, sessions) {
    state.sessions = sessions;
  },
  setSchedule(state, schedule) {
    state.schedule = schedule;
    const scheduleMapping = {};
    const sessionIdScheduleMapping = {};
    const filterTypeScheduleMapping = {};
    for (const scheduleObj of schedule) {
      scheduleMapping[scheduleObj.id] = scheduleObj;
      if (scheduleObj.sessionId) {
        sessionIdScheduleMapping[scheduleObj.sessionId] = scheduleObj;
      }
      if (!(scheduleObj.gicConferenceId in filterTypeScheduleMapping)) {
        filterTypeScheduleMapping[scheduleObj.gicConferenceId] = []
      }
      filterTypeScheduleMapping[scheduleObj.gicConferenceId].push(scheduleObj)
    }
    state.scheduleMapping = scheduleMapping;
    state.sessionIdScheduleMapping = sessionIdScheduleMapping;
    state.filterTypeScheduleMapping = filterTypeScheduleMapping;
  },
  setSearchSuggestions(state, searchSuggestions) {
    state.searchSuggestions = searchSuggestions;
  },
  // TODO refactor to split to use set selected filter instead
  setShowFilterModal(state, { showFilterModal }) {
    state.showFilterModal = showFilterModal;
  },
  setFilteredItems(state, { filteredItems }) {
    state.filteredItems = filteredItems;
  },
  setFilteredResources(state) {
    if (state.selectedFilter.type === "Sessions") {
      const filteredSessionIds = state.filteredItems;
      // filteredResource object format
      // "<session id>:[resources]"
      const filteredResources = {};
      for (const resource of state.flattenedAllResources) {
        for (const filteredSessionId of filteredSessionIds) {
          if (
            !resource.sessionIds ||
            !resource.sessionIds.includes(filteredSessionId)
          ) {
            continue;
          }

          const readableSessionId =
            state.scheduleMapping[filteredSessionId].sessionId;
          const readableSessionName =
            state.scheduleMapping[filteredSessionId].title;

          if (!(readableSessionId in filteredResources)) {
            filteredResources[readableSessionId] = {
              sessionId: readableSessionId,
              name: readableSessionName,
              resources: [],
            };
          }

          filteredResources[readableSessionId].resources.push(resource);

          const existSlug = new Set();
          filteredResources[readableSessionId].resources = filteredResources[
            readableSessionId
          ].resources.filter((item) => {
            if (existSlug.has(item.slug)) return false;
            existSlug.add(item.slug);
            return true;
          });
        }
      }
      state.filteredResources = filteredResources;

      const filteredBooks = state.flattenedBooks.filter((resource) => {
        const isSessionsMatch =
          filteredSessionIds.length > 0
            ? filteredSessionIds.some((id) => resource.sessionId === id)
            : true;
        return isSessionsMatch;
      });
      state.filteredBooks = filteredBooks;
    } 
    state.showFilterModal = true;
  },
  clearState(state) {
    state = {
      allResources: [],
      allBooks: [],
      flattenedAllResources: [],
      flattenedBooks: [],
      sessions: [],
      filteredItems: [],
      filteredResources: {},
      showFilterModal: false,
      schedule: [],
      searchSuggestions: [],
      selectedFilter: {},
    };
  },
  clearFilteredResources(state) {
    state.filteredResources = {}
  },
  toggleBookMarkList(state, { type, slug, flag }) {
    if (flag) {
      let item = null;
      const itemList = state.allResources
        .map((section) => section.resources.filter((r) => r.slug === slug))
        .flat();
      if (itemList.length > 0) {
        item = itemList[0];
      }

      // if all resource can't find, find from watchlist
      if (!item) {
        const itemListWatchList = state.allWatchLists
          .map((section) => section.resources.filter((r) => r.slug === slug))
          .flat();
        if (itemListWatchList.length > 0) {
          item = itemListWatchList[0];
        }
      }

      if (item) {
        state.allBookMarkList[0].resources.unshift(item);
      }
    } else {
      state.allBookMarkList.forEach((section) => {
        section.resources = section.resources.filter((r) => r.slug !== slug);
      });
    }
  },
  setSelectedFilter(state, selectedFilter) {
    state.selectedFilter = selectedFilter;
  },
  setClickedFilter(state, clickedFilter) {
    state.clickedFilter = clickedFilter;
  },
  setBookMarkMapItem(state, { slug, existSlug }) {
    const newBookMarkMap = state.bookMarkMap;
    if (!existSlug) {
      delete newBookMarkMap[slug];
    } else {
      newBookMarkMap[slug] = existSlug;
    }
    state.bookMarkMap = newBookMarkMap;
  },
};

const actions = {
  async fetchAllResources(
    { state, dispatch, commit, rootState },
    { locale = "en", conferenceType = "Global" }
  ) {
    commit("setIsLoading", true);
    try {
      const accessToken = rootState.user.token;
      const { data: allresources } = await axios.get(
        process.env.VUE_APP_API_URL + "/cms/gic/resources/all",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            locale: locale,
            conferenceType: conferenceType,
          },
        }
      );
      const allBooks = [];
      const allResources = [];
      for (const resource of allresources) {
        const firstResource = resource.resources.at(0);
        if (firstResource) {
          switch (firstResource.type) {
            case "sm_books":
              allBooks.push(resource);
              break;
            default:
              allResources.push(resource);
          }
        } else {
          allResources.push(resource);
        }
      }
      commit("setAllResources", allResources);
      commit("setAllBooks", allBooks);

      const resourceList = [];
      for (let material of allResources) {
        for (let resource of material.resources) {
          Object.defineProperty(resource, "name", { value: material.name });
          resourceList.push(resource);
        }
      }
      commit("setFlattenedAllResources", resourceList);


      const bookList = [];
      for (let material of allBooks) {
        for (let resource of material.resources) {
          bookList.push(resource);
        }
      }
      commit("setFlattenedBooks", bookList);
    } catch (e) {
      dispatch("toast/error", "Failed to fetch session materials.", {
        root: true,
      });
      throw e;
    } finally {
      commit("setIsLoading", false);
    }
  },
  async fetchBookMarkLists(
    { state, dispatch, commit, rootState },
    { locale = "en", conferenceType = "Global" }
  ) {
    commit("setIsLoading", true);
    try {
      const accessToken = rootState.user.token;
      const { data: bookMarkList } = await axios.get(
        process.env.VUE_APP_API_URL + "/cms/gic/resources/my-lists",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            locale: locale,
            conferenceType: conferenceType,
          },
        }
      );
      commit("setAllBookMarkList", bookMarkList);
      if (
        bookMarkList &&
        bookMarkList.length > 0 &&
        bookMarkList[0].resources &&
        bookMarkList[0].resources.length > 0
      ) {
        for (const resource of bookMarkList[0].resources) {
          commit("setBookMarkMapItem", {
            slug: resource.slug,
            existSlug: true,
          });
        }
      }
    } catch (e) {
      dispatch("toast/error", "Failed to fetch watch lists.", {
        root: true,
      });
    } finally {
      commit("setIsLoading", false);
    }
  },
  async fetchSchedule(
    { commit, dispatch, rootState },
    { locale = "en", conferenceType = "Global" }
  ) {
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        process.env.VUE_APP_API_URL + "/cms/gic/schedules/session/all",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            locale: locale,
            conferenceType: conferenceType,
          },
        }
      );
      if (status !== 200) throw new Error("Failed in getting schedule");
      commit("setSchedule", data);
    } catch (e) {
      dispatch("toast/error", "Failed to fetch schedule.", {
        root: true,
      });
      throw e;
    }
  },
  async fetchSearchSuggestions(
    { commit, dispatch, rootState },
    { locale = "en", conferenceType = "Global" }
  ) {
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        process.env.VUE_APP_API_URL + "/cms/gic/search/keywords",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            locale: locale,
            conferenceType: conferenceType,
          },
        }
      );
      if (status !== 200) throw new Error("Failed in getting search keywords");
      commit("setSearchSuggestions", data);
    } catch (e) {
      dispatch("toast/error", "Failed to fetch schedule.", {
        root: true,
      });
      throw e;
    }
  },

  updateShowFilterModal({ commit }, { showFilterModal }) {
    commit("setShowFilterModal", { showFilterModal });
  },

  updateFilteredItems({ commit }, { filteredItems }) {
    commit("setFilteredItems", { filteredItems });
  },
  updateFilteredResources({ commit }) {
    commit("setFilteredResources");
  },

  clearState({ commit }) {
    commit("clearState");
  },
  toggleBookMark({ commit, state }, { type, slug }) {
    // TODO remove type
    const existFlag = slug in state.bookMarkMap;
    commit("setBookMarkMapItem", { slug, existSlug: !existFlag });
  },
  toggleBookMarkList({ commit }, { type, slug, flag }) {
    commit("toggleBookMarkList", { type, slug, flag });
  },
  setSelectedFilter({ commit }, selectedFilter) {
    commit("setSelectedFilter", selectedFilter);
  },
  setClickedFilter({ commit }, clickedFilter) {
    commit("setClickedFilter", clickedFilter);
  },
};

const getters = {
  myListHeader(state) {
    return state.allBookMarkList !== "undefined" &&
      state.allBookMarkList &&
      state.allBookMarkList.length > 0
      ? state.allBookMarkList[0].name
      : "";
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
